import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { DeviceIDBadge, CriticalityBadge } from './Badges';
import { AvailabilityImpactBadge } from './AvailabilityImpactBadge';
import { deviceHasAvailabilityImpact } from '../../constants/availabilityImpactFilter';
import GuestPageHeader from './GuestPageHeader';
import Footer from './Footer';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { formatPhoneNumber, removeCountryCode, validateIndianPhoneNumber } from '../../../utils/phoneFormatter';
import ComplaintPhotoUpload, {
  uploadComplaintPhotos,
  buildConfirmationPhotoThumbnails,
  revokePhotoPreviews,
} from '../../../features/complaints/components/ComplaintPhotoUpload';
import './GuestComplaintForm.css';

function GuestComplaintForm({ device, locationName: initialLocationName = null, onSuccess, onCancel }) {
  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    category_id: '',
    title: '',
    problem_details: '',
    breakdown_since: getCurrentDate(),  // Auto-set to current date
    reporter_employee_id: '',
    reporter_full_name: '',
    reporter_email: '',
    reporter_contact_number: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const selectedCategory = categories.find((c) => String(c.id) === String(formData.category_id));
  const isSystemCategorySelected = !!selectedCategory?.is_system;
  const [employeeVerification, setEmployeeVerification] = useState({
    status: 'idle', // idle, loading, found, not_found
    error: null,
    data: null
  });
  const [verificationTimeout, setVerificationTimeout] = useState(null);
  const [organizationName, setOrganizationName] = useState(null);
  const [locationName, setLocationName] = useState(initialLocationName);
  const submitAttemptedRef = useRef(false);

  // Fetch organization name on mount
  useEffect(() => {
    const fetchOrganizationName = async () => {
      try {
        if (!device?.organization_id) {
          console.warn('No organization_id available in device data');
          return;
        }

        const response = await fetch(API_ENDPOINTS.ORGANIZATIONS.GET(device.organization_id));
        if (response.ok) {
          const data = await response.json();
          if (data && data.name) {
            setOrganizationName(data.name);
          }
        }
      } catch (error) {
        console.error('Error fetching organization name:', error);
        // Continue without organization name
      }
    };

    fetchOrganizationName();
  }, [device]);

  // Fetch complaint categories applicable to this device (guest-accessible endpoint)
  useEffect(() => {
    const fetchCategories = async () => {
      if (!device?.device_identifier) {
        setCategoriesLoading(false);
        return;
      }
      setCategoriesLoading(true);
      setCategoriesError(null);
      try {
        const response = await fetch(
          API_ENDPOINTS.COMPLAINT_CATEGORIES.APPLICABLE(`device_identifier=${encodeURIComponent(device.device_identifier)}`)
        );
        if (response.ok) {
          const data = await response.json();
          setCategories(Array.isArray(data) ? data : []);
        } else {
          setCategoriesError('Failed to load complaint categories');
          setCategories([]);
        }
      } catch (error) {
        console.error('Error fetching complaint categories:', error);
        setCategoriesError('Failed to load complaint categories');
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [device]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    // Apply phone validation for contact number (same as UserFormModal.js)
    if (name === 'reporter_contact_number') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      // Prevent number starting with 0 - if user tries to enter 0, don't add it
      if (digits.length === 1 && digits === '0') {
        finalValue = '';
      } else if (digits.length > 1 && digits.startsWith('0')) {
        // If user pastes a number starting with 0, reject the entire input
        finalValue = '';
      } else {
        finalValue = digits;
      }
    }

    if (name === 'category_id') {
      const previousCategory = categories.find((c) => String(c.id) === String(formData.category_id));
      const nextCategory = categories.find((c) => String(c.id) === String(finalValue));
      const previousLabel = (previousCategory?.label || '').trim();
      const nextLabel = (nextCategory?.label || '').trim();
      const titleWasAutoOrEmpty =
        !formData.title.trim() ||
        formData.title.trim() === previousLabel;

      setFormData((prev) => {
        let nextTitle = prev.title;
        if (!finalValue) {
          nextTitle = titleWasAutoOrEmpty ? '' : prev.title;
        } else if (nextCategory?.is_system) {
          nextTitle = titleWasAutoOrEmpty ? '' : prev.title;
        } else if (titleWasAutoOrEmpty) {
          nextTitle = nextLabel;
        }
        return {
          ...prev,
          category_id: finalValue,
          title: nextTitle,
        };
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));

    // Trigger employee verification when employee_id changes
    if (name === 'reporter_employee_id') {
      // Clear previous employee details when user changes the ID
      setFormData(prev => ({
        ...prev,
        reporter_full_name: '',
        reporter_email: '',
        reporter_contact_number: ''
      }));
      verifyEmployee(value);
    }
  };

  const verifyEmployee = (employeeId) => {
    // Clear previous timeout
    if (verificationTimeout) {
      clearTimeout(verificationTimeout);
    }

    if (!employeeId || employeeId.trim() === '') {
      setEmployeeVerification({
        status: 'idle',
        error: null,
        data: null
      });
      return;
    }

    // Set loading state
    setEmployeeVerification({
      status: 'loading',
      error: null,
      data: null
    });

    // Debounce the API call (wait 500ms after user stops typing)
    const timeout = setTimeout(async () => {
      try {
        if (!device?.organization_id) {
          console.warn('No organization_id available in device data');
          setEmployeeVerification({
            status: 'idle',
            error: 'Organization information missing',
            data: null
          });
          return;
        }

        const response = await fetch(
          API_ENDPOINTS.USERS.LOOKUP_BY_EMPLOYEE_ID(employeeId.trim()),
          {
            headers: {
              'X-Organization-Id': String(device.organization_id),
              'X-Location-Id': String(device.location_id || device.locationId || device.location || '')
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Employee found - auto-populate their details
          // Strip country code from contact number to get just the 10 digits (same as UserFormModal.js)
          const contactNumber = data.contact_number ? removeCountryCode(data.contact_number) : '';
          setFormData(prev => ({
            ...prev,
            reporter_employee_id: data.employee_id || prev.reporter_employee_id,
            reporter_full_name: data.full_name || '',
            reporter_email: data.email || '',
            reporter_contact_number: contactNumber
          }));

          setEmployeeVerification({
            status: 'found',
            error: null,
            data: data
          });
        } else if (response.status === 404) {
          setEmployeeVerification({
            status: 'not_found',
            error: null,
            data: null
          });
        } else if (response.status === 400) {
          try {
            const err = await response.json();
            setEmployeeVerification({
              status: 'invalid_location',
              error: err?.message || err?.error || 'Employee not valid for this location',
              data: null
            });
          } catch (parseErr) {
            setEmployeeVerification({
              status: 'invalid_location',
              error: 'Employee not valid for this location',
              data: null
            });
          }
        } else {
          setEmployeeVerification({
            status: 'idle',
            error: 'Error verifying employee',
            data: null
          });
        }
      } catch (error) {
        console.error('Error verifying employee:', error);
        setEmployeeVerification({
          status: 'idle',
          error: 'Error verifying employee',
          data: null
        });
      }
    }, 500);

    setVerificationTimeout(timeout);
  };

  const validateForm = () => {
    if (employeeVerification.error) {
      return false;
    }

    // Category is always required
    if (!formData.category_id) {
      return false;
    }

    // Title and details are only required when the "Other" system category is selected
    if (isSystemCategorySelected) {
      if (!formData.title.trim()) {
        return false;
      }
      if (!formData.problem_details.trim()) {
        return false;
      }
    }

    // Breakdown since is auto-set to current date - no validation needed

    // Reporter employee ID is required
    if (!formData.reporter_employee_id.trim()) {
      return false;
    }

    // Reporter full name is required
    if (!formData.reporter_full_name.trim()) {
      return false;
    }

    // Reporter email is required and must be valid
    if (!formData.reporter_email.trim()) {
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.reporter_email)) {
      return false;
    }

    // Reporter contact number is required when employee is not found
    // Validate phone number using the same utility function as UserFormModal.js
    if (employeeVerification.status !== 'found') {
      if (!formData.reporter_contact_number.trim()) {
        return false;
      }

      const validation = validateIndianPhoneNumber(formData.reporter_contact_number);
      if (!validation.isValid) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Breakdown date is auto-set to current date, no need to validate it

    if (!validateForm()) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    // Check if employee verification is still in progress
    if (employeeVerification.status === 'loading') {
      setSubmitError('Please wait for employee verification to complete');
      return;
    }

    // If employee was looked up but not found, user MUST provide manual details
    if (employeeVerification.status === 'not_found') {
      if (!formData.reporter_full_name.trim() || !formData.reporter_email.trim() || !formData.reporter_contact_number.trim()) {
        setSubmitError('Please provide full name, email, and contact number for non-registered employee');
        return;
      }
    }

    try {
      // Prevent accidental double-submit (rapid clicks on button)
      if (submitAttemptedRef.current) {
        setSubmitError('Please wait - submission in progress');
        return;
      }

      if (device?.device_status && device.device_status !== 'active') {
        const transferDest =
          device.transferring_to_location_name || device.transferring_to_department_name;
        const transferMsg = transferDest
          ? `This device is transferring to ${transferDest} and cannot accept new complaints.`
          : 'This device is transferring and cannot accept new complaints right now.';
        setSubmitError(
          device.device_status === 'in_transfer'
            ? transferMsg
            : 'This device is not available for reporting right now.'
        );
        return;
      }

      submitAttemptedRef.current = true;
      setIsSubmitting(true);

      const response = await fetch(API_ENDPOINTS.COMPLAINTS.CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device_id: device.id,
          device_identifier: device.device_identifier,
          ...formData
        })
      });

      if (response.ok) {
        const result = await response.json();

        let photoUploadWarning = null;
        let attachedPhotos = [];
        if (photos.length > 0 && result.id) {
          const { failed, succeeded } = await uploadComplaintPhotos(result.id, photos, {
            photoUploadToken: result.photo_upload_token,
          });
          if (failed.length > 0) {
            photoUploadWarning = `Complaint created, but ${failed.length} photo(s) failed to upload.`;
            toast.warn(photoUploadWarning);
          }
          attachedPhotos = await buildConfirmationPhotoThumbnails(succeeded);
          revokePhotoPreviews(photos);
          setPhotos([]);
        }

        if (result.assigned_employee_id) {
          const who = result.assigned_employee_name || result.assigned_employee_id;
          toast.success(`Complaint reported successfully! Assigned to device technician (${who}).`);
        } else {
          toast.success('Complaint reported successfully!');
        }

        // NOTE: guest_device_data is kept in sessionStorage by ConfirmationPageHandler
        // GuestComplaintConfirmation will read organization_id from guest_device_data
        // No need to store organization_id separately

        onSuccess({
          id: result.id,
          complaint_identifier: result.complaint_identifier,
          photoUploadWarning,
          attachedPhotos,
          reporter_full_name: formData.reporter_full_name,
          reporter_email: formData.reporter_email,
          reporter_contact_number: formData.reporter_contact_number,
          reporter_role: result.reporter_role,
          assigned_employee_id: result.assigned_employee_id,
          assigned_employee_name: result.assigned_employee_name,
          assigned_employee_email: result.assigned_employee_email,
          assigned_employee_contact: result.assigned_employee_contact,
          assigned_employee_role: result.assigned_employee_role
        });
      } else {
        let message = 'Failed to report complaint';
        try {
          const error = await response.json();
          message = error.error || message;
          if (
            error.device_status === 'in_transfer' ||
            /must be in ["']?active["']? status/i.test(message)
          ) {
            message = device?.transferring_to_location_name || device?.transferring_to_department_name
              ? `This device is transferring to ${
                  device.transferring_to_location_name || device.transferring_to_department_name
                } and cannot accept new complaints.`
              : 'This device is locked while transferring and cannot accept new complaints.';
          }
        } catch (parseError) {
          // keep default message
        }
        setSubmitError(message);
        submitAttemptedRef.current = false;
      }
    } catch (error) {
      console.error('Error submitting complaint:', error);
      setSubmitError('Error submitting complaint. Please try again.');
      submitAttemptedRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="guest-page-layout">
      {/* Header */}
      <GuestPageHeader organizationName={organizationName} locationName={locationName} />

      {/* Main Content */}
      <main className="guest-page-main guest-complaint-main">
        <div className="guest-complaint-form-container">
          <div className="guest-complaint-form-card">
        {/* Header */}
        <div className="modal-header-extended">
          <div className="modal-header-title">
            <h3>Report Device Breakdown</h3>
            <div className="modal-device-info">
              <div className="modal-device-title-row">
                <span className="device-name">{device.name}</span>
                <DeviceIDBadge deviceId={device.device_identifier || device.device_id} size="small" />
              </div>
              <div className="modal-device-badges">
                {device.criticality && (
                  <CriticalityBadge criticality={device.criticality} size="small" />
                )}
                {deviceHasAvailabilityImpact(device) && (
                  <AvailabilityImpactBadge deviceHasImpact size="small" />
                )}
                {device.department && (
                  <span className="header-department">
                    <span className="department-icon" aria-hidden="true">
                      {device.department_icon || '🏥'}
                    </span>
                    <span className="device-department">
                      <span className="department-name">{device.department}</span>
                    </span>
                  </span>
                )}
                {device.physical_location && (
                  <span className="device-location">
                    <span className="location-icon" aria-hidden="true">📍</span>
                    <span className="location-name">{device.physical_location}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="close-btn" onClick={onCancel} type="button">✕</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="complaint-form">
          {/* Category */}
          <div className="form-group">
            <label htmlFor="category_id">Category *</label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
              disabled={categoriesLoading}
              autoFocus
            >
              <option value="">
                {categoriesLoading ? 'Loading categories...' : 'Select a category'}
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.label}</option>
              ))}
            </select>
            {categoriesError && (
              <small className="form-error-text" style={{ color: '#d32f2f' }}>{categoriesError}</small>
            )}
          </div>

          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">Title {isSystemCategorySelected ? '*' : ''}</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Device not powering on"
              required={isSystemCategorySelected}
            />
            {!isSystemCategorySelected && formData.category_id ? (
              <small className="form-help-text">Pre-filled from category — you can change it if needed</small>
            ) : isSystemCategorySelected ? (
              <small className="form-help-text">Enter a short title</small>
            ) : null}
          </div>

          {/* Details */}
          <div className="form-group">
            <label htmlFor="problem_details">Details {isSystemCategorySelected ? '*' : ''}</label>
            <textarea
              id="problem_details"
              name="problem_details"
              value={formData.problem_details}
              onChange={handleChange}
              placeholder="Describe the issue in detail..."
              rows="4"
              required={isSystemCategorySelected}
            />
          </div>

          {/* Hidden Breakdown Since field - auto-set to current date */}
          <input
            type="hidden"
            id="breakdown_since"
            name="breakdown_since"
            value={formData.breakdown_since}
          />

          {/* UNAUTHENTICATED REPORTER SECTION */}
          <div>
            <label className="reporter-section-label">👤 Reported By *</label>
            <div className="reporter-identification-section">
              <p className="section-hint">
                Help us contact you if we need more information about this issue
              </p>

              {/* Employee ID Input */}
            <div className="form-group">
              <label htmlFor="reporter_employee_id">Employee ID *</label>
              <input
                type="text"
                id="reporter_employee_id"
                name="reporter_employee_id"
                value={formData.reporter_employee_id}
                onChange={handleChange}
                placeholder="e.g., DHI10"
                required
                className={`employee-id-input ${
                  employeeVerification.status === 'loading' ? 'loading' : ''
                } ${employeeVerification.status === 'found' ? 'found' : ''} ${
                  employeeVerification.status === 'not_found' ? 'not-found' : ''
                }`}
              />

              {/* Employee Verification Indicator */}
              {employeeVerification.status === 'loading' && (
                <div className="verification-indicator loading">⟳ Verifying...</div>
              )}
              {employeeVerification.status === 'found' && (
                <div className="verification-indicator found">✓ Employee found - details auto-populated</div>
              )}
              {employeeVerification.status === 'not_found' && !employeeVerification.error && (
                <div className="verification-indicator not-found">
                  ⓘ Employee not in system - please enter details manually
                </div>
              )}
              {employeeVerification.error && (
                <div className="verification-indicator not-found">
                  ⓘ {employeeVerification.error}
                </div>
              )}
            </div>

            {/* If employee not found, show manual entry fields */}
            {employeeVerification.status !== 'found' && employeeVerification.status !== 'invalid_location' && (
              <div className="manual-entry-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reporter_full_name">Full Name *</label>
                    <input
                      type="text"
                      id="reporter_full_name"
                      name="reporter_full_name"
                      value={formData.reporter_full_name}
                      onChange={handleChange}
                      placeholder="e.g., John Doe"
                      required={employeeVerification.status !== 'found'}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="reporter_email">Email *</label>
                    <input
                      type="email"
                      id="reporter_email"
                      name="reporter_email"
                      value={formData.reporter_email}
                      onChange={handleChange}
                      placeholder="e.g., john@example.com"
                      required={employeeVerification.status !== 'found'}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reporter_contact_number">Contact Number *</label>
                  <div className="phone-input-wrapper">
                    <input
                      type="tel"
                      id="reporter_contact_number"
                      name="reporter_contact_number"
                      value={formData.reporter_contact_number}
                      onChange={handleChange}
                      placeholder="e.g., 9876543210"
                      required={employeeVerification.status !== 'found'}
                      maxLength="10"
                    />
                    <div className="phone-validation-display">
                      {(() => {
                        const digits = formData.reporter_contact_number ? formData.reporter_contact_number.replace(/\D/g, '') : '';
                        const validation = formData.reporter_contact_number ? validateIndianPhoneNumber(formData.reporter_contact_number) : { isValid: true, error: null };
                        const statusColor = formData.reporter_contact_number && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';

                        return (
                          <>
                            <span className="digit-count" style={{ color: statusColor }}>
                              {digits.length}/10
                            </span>
                            {formData.reporter_contact_number && (
                              <span className="validation-status" style={{ color: statusColor }}>
                                {!validation.isValid ? '✗' : digits.length === 10 ? '✓' : '⚠'}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  {formData.reporter_contact_number && (() => {
                    const validation = validateIndianPhoneNumber(formData.reporter_contact_number);
                    return (
                      <>
                        {validation.error && (
                          <small className="form-error-text" style={{ color: '#d32f2f' }}>
                            {validation.error}
                          </small>
                        )}
                        {validation.isValid && (
                          <small className="form-success-text" style={{ color: '#27ae60' }}>
                            ✓ {formatPhoneNumber(formData.reporter_contact_number)}
                          </small>
                        )}
                      </>
                    );
                  })()}
                  <small className="form-help-text">Indian format: 10-digit mobile number (must start with 6, 7, 8, or 9)</small>
                </div>
              </div>
            )}

            {/* If employee found, show read-only display */}
            {employeeVerification.status === 'found' && (
              <div className="employee-details-found">
                <div className="detail-row">
                  <span className="detail-label">👤 Name:</span>
                  <span className="detail-value">{formData.reporter_full_name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">✉️ Email:</span>
                  <span className="detail-value">{formData.reporter_email}</span>
                </div>
                {formData.reporter_contact_number && (
                  <div className="detail-row">
                    <span className="detail-label">📞 Contact:</span>
                    <span className="detail-value">{formData.reporter_contact_number}</span>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          <ComplaintPhotoUpload
            photos={photos}
            disabled={isSubmitting}
            preferCamera
            onChange={(next, err) => {
              setPhotos(next);
              setPhotoError(err);
            }}
          />
          {photoError && (
            <div className="complaint-submit-error" role="alert">{photoError}</div>
          )}

          {submitError && (
            <div className="complaint-submit-error" role="alert">
              {submitError}
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="action-btn action-secondary" onClick={onCancel} disabled={isSubmitting}>
              <span className="action-icon">←</span>
              <span className="action-text">Back to Device</span>
            </button>
            <button
              type="submit"
              className="action-btn action-primary"
              disabled={
                isSubmitting ||
                employeeVerification.status === 'loading' ||
                employeeVerification.status === 'invalid_location' ||
                !!employeeVerification.error
              }
              title={
                (employeeVerification.status === 'invalid_location' || employeeVerification.error)
                  ? (employeeVerification.error || 'Employee not valid for this location')
                  : undefined
              }
            >
              <span className="action-icon">📝</span>
              <span className="action-text">{isSubmitting ? 'Submitting...' : 'Report Breakdown'}</span>
            </button>
          </div>
        </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default GuestComplaintForm;
